import {
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  entityPresentationApiRef,
  useEntity,
  useRelatedEntities,
} from '@backstage/plugin-catalog-react';
import { EntityRefLinkWithVuid } from '../../VuidChip';
import { useApiHolder } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import React from 'react';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import Popper, { PopperProps } from '@material-ui/core/Popper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { MTableToolbar } from '@material-table/core';
import {
  InfoCard,
  InfoCardVariants,
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
  TableOptions,
} from '@backstage/core-components';
import { ARCHIVED_TAG } from '../../input/ArchivedPicker';

/**
 * The name of an entity as a link, with the icon of what it is in front of it -
 * the app gives a department a different one than a team, a cluster a different
 * one than a slice of one, see `iconOf` in the app.
 *
 * The stock column of `EntityTable` hands `EntityRefLink` the title of the
 * entity, and a link that is given its text renders that text and nothing
 * else, which leaves out the icon. Sorting and searching go by the same name
 * that is shown.
 */
export function createEntityNameColumn<T extends Entity>(
  defaultKind: string,
): TableColumn<T> {
  const nameOf = (entity: T) => entity.metadata.title ?? entity.metadata.name;
  return {
    title: 'Name',
    field: 'metadata.name',
    highlight: true,
    defaultSort: 'asc',
    customSort: (one, other) => nameOf(one).localeCompare(nameOf(other)),
    // The vuid is searched too: it stands next to the name, see the chip.
    customFilterAndSearch: (query, entity) =>
      [nameOf(entity), entity.metadata.name].some(it =>
        it
          .toLocaleLowerCase('en-US')
          .includes(query.toLocaleLowerCase('en-US')),
      ),
    // The vuid of a team or a product follows as a chip that copies it.
    render: entity => (
      <EntityRefLinkWithVuid entityRef={entity} defaultKind={defaultKind} />
    ),
  };
}

/**
 * A column a table can be narrowed down by, on top of what a column is
 * otherwise.
 *
 * `filterValueOf` is the one value of a row the dropdown of the column lists
 * and picks by. It is asked for rather than read off the field because what a
 * cell shows is not always what the field holds: the environment is the first
 * of the tags of an entity, and the rest of them have no business in a
 * dropdown of environments.
 *
 * `filterValueKind` says that those values name entities of that kind, which
 * is what the product and the team columns hold. The values are then vuids and
 * the dropdown shows the titles behind them instead, the way the cells do.
 *
 * `filterValueNone` is the value a row that says nothing about the column
 * carries, together with what the dropdown is to write for it: the wording the
 * cell of such a row uses, "No Product" rather than the bare `unknown` the
 * value is. Nothing is looked up behind it, there is no entity to find.
 */
export type FilterableColumn<T extends Entity> = TableColumn<T> & {
  filterValueOf?: (entity: T) => string;
  filterValueKind?: string;
  filterValueNone?: { value: string; label: string };
};

/**
 * The value a row that says nothing about a column carries. A dropdown lists
 * the values of its rows and a row that holds none would fall out of it
 * altogether, so it is given one - and the same one everywhere, so that "says
 * nothing" reads alike in every table.
 */
export const UNKNOWN = 'unknown';

/**
 * What a row that says nothing is called, where the column has nothing better
 * to call it than that: `unknown`, the way an empty cell reads elsewhere. The
 * cell and the dropdown of the column are both given it, so the same row is
 * called the same thing in both places.
 */
export const NOTHING_KNOWN = { value: UNKNOWN, label: UNKNOWN };

/** What is not known is said so in grey, the way an empty cell is elsewhere. */
export function orUnknown(value: string) {
  if (value === UNKNOWN) {
    return <span style={{ color: '#888' }}>{UNKNOWN}</span>;
  }
  return <>{value}</>;
}

/**
 * The options of a table that is narrowed down by its filter row.
 *
 * A department gathers the clusters of every team below it and a large one has
 * hundreds of them, which is a page of scrolling before whatever card comes
 * next. Ten rows keep the table the size of the cards around it and the picker
 * goes up from there.
 *
 * No search box in the corner: every column carries its own filter, the name
 * among them, and the two of them next to each other only ask which one to
 * type into. Both go over all the rows, not over the page on screen.
 */
export const FILTER_TABLE_OPTIONS: TableOptions = {
  search: false,
  paging: true,
  pageSize: 10,
  pageSizeOptions: [10, 20, 50, 100],
  // Otherwise a last page that is not full is padded out with empty rows.
  emptyRowsWhenPaging: false,
};

/**
 * The stock toolbar, styled the way `Table` styles its own. That one is not
 * exported from `@backstage/core-components`, so its three rules are repeated
 * here rather than a table that brings its own toolbar looking unlike every
 * other table in the app (`StyledMTableToolbar` in core-components' `Table.tsx`).
 */
export const StyledMTableToolbar = withStyles(
  theme => ({
    root: { padding: theme.spacing(3, 0, 2.5, 2.5) },
    title: { '& > h6': { fontWeight: theme.typography.fontWeightBold } },
    searchField: { paddingRight: theme.spacing(2) },
  }),
  { name: 'BackstageTableToolbar' },
)(MTableToolbar);

/**
 * A toolbar whose title says how many rows the table is showing.
 *
 * The count cannot be written into the title the table is given: the row of
 * dropdowns below the headers narrows the rows down inside
 * `@material-table/core`, and a title handed in from outside would keep saying
 * how many there were before. What is left of the rows is handed to the
 * toolbar as `data` - all pages of it, filters and search applied - which is
 * why the title is built here.
 */
function countingToolbar(title: string) {
  return function CountingToolbar(props: { data: unknown[] }) {
    return (
      <StyledMTableToolbar
        {...props}
        // The heading `Table` builds for a title of its own, see its `title`.
        title={
          <Typography variant="h5" component="h2">
            {`${title} (${props.data.length})`}
          </Typography>
        }
      />
    );
  };
}

/**
 * What `@material-table/core` hands the filter component of a column. Its own
 * types say less than it passes - `tableData` is not in them, and the id it
 * wants back is a number rather than the string they promise - so what is
 * actually handed over is written out here.
 */
type LookupFilterProps = {
  columnDef: {
    tableData: { id: number; filterValue?: string[] };
    lookup: Record<string, string>;
    filterPlaceholder?: string;
    /** How many rows a value of this column stands for - see filterCountsOf. */
    filterCountOf: (value: string) => number;
    /**
     * Which values are still worth offering, given what the other columns have
     * been narrowed down to, and what is ticked here - see withFilterRow.
     */
    filterOptionsOf: (picked: string[]) => string[];
    /** Says upwards what has been ticked here, so the other columns can follow. */
    onPickedChanged: (values: string[]) => void;
  };
  onFilterChanged: (id: number, values: string[]) => void;
};

/**
 * The list of a dropdown, no narrower than the stock one was - 250px, see
 * `MenuProps` in `LookupFilter`. Left to itself it is as wide as the column it
 * hangs under, and a narrow one - Type, Environment - would cut the team and
 * product titles of the wider ones in half.
 */
function WideListPopper(props: PopperProps) {
  return (
    <Popper
      {...props}
      style={{ ...props.style, minWidth: 250 }}
      placement="bottom-start"
    />
  );
}

/**
 * The dropdown of a column, with a box to type in.
 *
 * The stock one - `LookupFilter` in `@material-table/core` - is a plain select:
 * every value the rows hold, in one list, and nothing but scrolling to get
 * through it. A department holds hundreds of clusters over dozens of teams and
 * as many products, which is a long way to scroll for one of them. This is the
 * same list - the same values, the same labels, a tick against each - behind an
 * input that narrows it down as it is typed into.
 *
 * What is ticked goes to the table there and then, so the rows follow every
 * tick rather than waiting for the list to be closed again. It goes to the
 * other dropdowns as well, which offer what is left of the rows once this one
 * has had its say - see filterCountsOf.
 */
function LookupSearchFilter(props: LookupFilterProps) {
  const { columnDef, onFilterChanged } = props;
  const {
    lookup,
    filterPlaceholder,
    tableData,
    filterCountOf,
    filterOptionsOf,
    onPickedChanged,
  } = columnDef;

  const [picked, setPicked] = React.useState<string[]>(
    tableData.filterValue ?? [],
  );

  // The table empties the filters of columns it has rebuilt - see signatureOf
  // above - and the dropdown has to say the same as the rows on screen. The
  // other columns are told of it too: a filter emptied behind the back of the
  // dropdown widens what they offer, the same as one emptied by hand.
  React.useEffect(() => {
    const values = tableData.filterValue ?? [];
    setPicked(values);
    onPickedChanged(values);
  }, [tableData.filterValue, onPickedChanged]);

  const labelOf = (value: string) => lookup[value] ?? value;

  return (
    <Autocomplete
      multiple
      // A list that closes on the first tick makes picking two values twice
      // the work, and the select this replaces stayed open as well.
      disableCloseOnSelect
      size="small"
      PopperComponent={WideListPopper}
      options={filterOptionsOf(picked)}
      value={picked}
      getOptionLabel={labelOf}
      onChange={(_event, values) => {
        setPicked(values);
        onFilterChanged(tableData.id, values);
        onPickedChanged(values);
      }}
      // Chips - what is otherwise shown for what is picked - are a line of
      // their own each and push the table down as they pile up. The select
      // this replaces wrote the values out in one line, which is what the
      // filter row has room for.
      renderTags={values => <span>{values.map(labelOf).join(', ')}</span>}
      renderOption={(value, { selected }) => (
        <>
          <Checkbox size="small" checked={selected} />
          <span style={{ flexGrow: 1 }}>{labelOf(value)}</span>
          {/*
            How many rows the value stands for, at the end of the line and in
            grey: it says how much of the table is behind a value without
            standing in the way of reading down the list of values.
          */}
          <span style={{ color: '#888', paddingLeft: 8 }}>
            {filterCountOf(value)}
          </span>
        </>
      )}
      renderInput={params => (
        <TextField
          {...params}
          // What the column calls an unnarrowed table, "All teams" - the label
          // the select carried. It goes once something is picked, where the
          // select put its values.
          placeholder={picked.length === 0 ? filterPlaceholder : undefined}
        />
      )}
    />
  );
}

/**
 * How many rows each value of each column stands for, one map per column in
 * the order the columns come in - a column that names no filter value of its
 * own gets an empty one.
 *
 * A value is counted over the rows the *other* columns are narrowed down to
 * rather than over all of them: with a product picked, the team dropdown says
 * how many of that product's clusters each team holds, and a team that holds
 * none of them is not counted at all - which is what takes it off the list, see
 * filterOptionsOf. The column's own filter is left out of the narrowing; a
 * column that counted itself would be left offering only what is already
 * ticked in it, and there would be no way back from a pick.
 *
 * It is still the one pass over the rows the values themselves are gathered in.
 * What each row says in each column is read once up front, so that asking it of
 * every column while counting every other one does not read it again each time.
 */
function filterCountsOf<T extends Entity>(
  columns: FilterableColumn<T>[],
  entities: T[],
  picked: string[][],
): Map<string, number>[] {
  const valuesOf = columns.map(column => column.filterValueOf);
  const rows = entities.map(entity =>
    valuesOf.map(valueOf => valueOf?.(entity)),
  );

  return columns.map((_column, index) => {
    const counts = new Map<string, number>();
    if (!valuesOf[index]) {
      return counts;
    }
    for (const row of rows) {
      const narrowedOut = valuesOf.some((valueOf, other) => {
        const ticked = picked[other];
        if (other === index || !valueOf || !ticked || ticked.length === 0) {
          return false;
        }
        return !ticked.includes(row[other]!);
      });
      if (narrowedOut) {
        continue;
      }
      const value = row[index]!;
      counts.set(value, (counts.get(value) ?? 0) + 1);
    }
    return counts;
  });
}

const NO_LABELS = new Map<string, string>();

/**
 * The entity ref a filter value stands for, when the column says its values
 * name entities.
 */
function refOfValue(kind: string, value: string): string {
  return stringifyEntityRef(parseEntityRef({ name: value, kind: kind }));
}

/**
 * What to write in the dropdowns, keyed by the entity ref of the value.
 *
 * A vuid is no help to anyone picking a product or a team out of a list, and
 * it is not what the cells of those columns show either. The titles are asked
 * of the same api the links in the cells go through, which holds them for the
 * table it has already drawn, so this costs a request only for what is not on
 * screen yet. Until they arrive - and where that api is not to be had at all,
 * as in a test that stands up nothing but a catalog - the value stands in for
 * itself.
 */
function useFilterLabels<T extends Entity>(
  columns: FilterableColumn<T>[],
  entities: T[],
): Map<string, string> {
  const presentationApi = useApiHolder().get(entityPresentationApiRef);

  const refs = new Set<string>();
  for (const column of columns) {
    if (!column.filterValueOf || !column.filterValueKind) {
      continue;
    }
    for (const entity of entities) {
      const value = column.filterValueOf(entity);
      // The value a row that says nothing carries names no entity, and asking
      // the catalog for one only fetches an answer nobody waits for.
      if (value === column.filterValueNone?.value) {
        continue;
      }
      refs.add(refOfValue(column.filterValueKind, value));
    }
  }
  // The refs themselves are what the answer depends on, and a new array of the
  // same ones must not send it off again.
  const wanted = [...refs].sort().join(' ');

  const { value } = useAsync(async () => {
    const asked = presentationApi && wanted ? wanted.split(' ') : [];
    const presentations = await Promise.all(
      asked.map(ref => presentationApi!.forEntity(ref).promise),
    );
    return new Map(
      asked.map((ref, index) => [ref, presentations[index].primaryTitle]),
    );
  }, [presentationApi, wanted]);

  return value ?? NO_LABELS;
}

/**
 * The columns as the table behind them wants them: a column that names the
 * value of its rows gets a dropdown of the values they actually hold, and a
 * column that names none gets the plain text box the table falls back to -
 * which is what a name, of which no two rows share one, is narrowed down by.
 */
function withFilterRow<T extends Entity>(
  columns: FilterableColumn<T>[],
  entities: T[],
  labels: Map<string, string>,
  counts: React.MutableRefObject<Map<string, number>[]>,
  onPicked: (index: number, values: string[]) => void,
): TableColumn<T>[] {
  return columns.map(
    ({ filterValueOf, filterValueKind, filterValueNone, ...column }, index) => {
      if (!filterValueOf) {
        return column;
      }
      const labelOf = (value: string) => {
        if (value === filterValueNone?.value) {
          return filterValueNone.label;
        }
        return (
          (filterValueKind && labels.get(refOfValue(filterValueKind, value))) ||
          value
        );
      };
      const values = [...new Set(entities.map(filterValueOf))].sort(
        (one, other) => labelOf(one).localeCompare(labelOf(other)),
      );
      const filterable = {
        ...column,
        lookup: Object.fromEntries(
          values.map(value => [value, labelOf(value)]),
        ),
        // The dropdown of the column, which is the stock select with a box to
        // type in - see LookupSearchFilter. The table takes the component off
        // the column but types it as taking less than it is handed, hence the
        // cast; the one below then puts the whole column back into shape.
        filterComponent:
          LookupSearchFilter as unknown as TableColumn<T>['filterComponent'],
        // Read through the ref rather than counted into the lookup above: a
        // count moves whenever the rows do - the archived switch is enough -
        // while the values behind them stay as they were, and a column that
        // says something new is a new column to the table, which costs the
        // filter row everything it holds. This way the numbers are read when
        // the dropdown draws them and the columns stay the columns.
        filterCountOf: (value: string) =>
          counts.current[index]?.get(value) ?? 0,
        // What the dropdown still has to offer: the values held by the rows
        // the other columns leave, which is what the counts were taken over -
        // a value nothing is counted for would only narrow the table down to
        // nothing. Read through the same ref and for the same reason.
        //
        // What is ticked here stays on the list whatever the others say. The
        // ticks are the only way back - what is picked is written out as text
        // rather than as chips to take off again - and a pick that took itself
        // off the list would be a table there is no widening again.
        filterOptionsOf: (ticked: string[]) => {
          const counted = counts.current[index];
          if (!counted) {
            return values;
          }
          return values.filter(
            value => counted.has(value) || ticked.includes(value),
          );
        },
        onPickedChanged: (ticked: string[]) => onPicked(index, ticked),
        // The dropdown hands over what is ticked, the search field the text
        // that was typed, and the same function answers both.
        customFilterAndSearch: (query: any, entity: T) =>
          Array.isArray(query)
            ? query.length === 0 || query.includes(filterValueOf(entity))
            : filterValueOf(entity)
                .toLocaleLowerCase('en-US')
                .includes(String(query).toLocaleLowerCase('en-US')),
      };
      return filterable as TableColumn<T>;
    },
  );
}

/**
 * What the columns above are built from, as a string that can be compared.
 *
 * The array they are is built afresh on every render, functions and all, and
 * `@material-table/core` takes an array it has not seen before for a change of
 * the columns themselves: it rebuilds them and throws away what the filter row
 * holds - see `setColumns` in its data manager. So the array is only built
 * again once what goes into it has actually changed, which is the columns, the
 * values their rows hold, and the labels of those values. The cell renderers
 * are left out: a column that renders differently says so in its title or its
 * field. A title that is not a word but a whole element - which is what the
 * stock columns of `EntityTable` carry - is left out with them; the field and
 * the place in the row say which column it is.
 */
function signatureOf<T extends Entity>(
  columns: FilterableColumn<T>[],
  entities: T[],
  labels: Map<string, string>,
): string {
  return JSON.stringify([
    columns.map(column => [
      typeof column.title === 'string' ? column.title : null,
      column.field,
      column.filterValueKind,
      column.filterValueNone,
      column.filterValueOf
        ? [...new Set(entities.map(column.filterValueOf))].sort()
        : null,
    ]),
    [...labels].sort(),
  ]);
}

/**
 * A table of entities that can be narrowed down.
 *
 * It is what `EntityTable` is - the same look, the same defaults - except for
 * the row of dropdowns below the headers, which is the only way to pick the
 * clusters that run on one thing, or in one environment, out of the hundreds
 * a large department holds. A table whose columns name no filter value of
 * their own has no such row and is `EntityTable` down to the pixel.
 *
 * It is also the one place every table of this plugin goes through, which is
 * why what has been wound up is left out here rather than in each caller.
 */
export function EntityFilterTable<T extends Entity>(props: {
  title: string;
  variant?: InfoCardVariants;
  columns: FilterableColumn<T>[];
  entities: T[];
  tableOptions?: TableOptions;
  /**
   * Which rows have been wound up. Left out unless the switch above the table
   * asks for them, and the switch only appears where a row was held back.
   *
   * A table says what it is by answering this itself: for a product, a team or
   * a department it is the tag the entity carries, for a resource it is the
   * product behind the row - a cluster carries no such tag of its own. A table
   * that gives no answer at all shows everything and offers no switch.
   */
  isArchived?: (entity: T) => boolean;
  /**
   * Whether the title says how many rows there are, as `Title (12)`. A table
   * that is paged shows ten of them at a time and how many pages there are is
   * no answer to how much a department holds, which is what the tabs of
   * clusters and cloud accounts are read for.
   *
   * The number is of the rows the table is showing: narrowing the table down
   * in the filter row brings it down with it, and switching the archived ones
   * on raises it.
   */
  countInTitle?: boolean;
}) {
  const {
    title,
    variant = 'gridItem',
    columns,
    entities,
    tableOptions = {},
    isArchived,
    countInTitle = false,
  } = props;

  const [showArchived, setShowArchived] = React.useState(false);

  // A listing is read as what there is, and a retired product among the live
  // ones of a team is not an answer to what that team runs.
  const held = isArchived ? entities.filter(isArchived) : [];
  const rows =
    showArchived || held.length === 0
      ? entities
      : entities.filter(entity => !isArchived!(entity));

  // The dropdowns offer what the rows on screen hold, so that switching the
  // archived ones on is what puts their values into the filters as well.
  const labels = useFilterLabels(columns, rows);

  // What is ticked in each column, in the order the columns come in. The table
  // below is the one that holds the filters and narrows the rows by them; this
  // is the same answer kept where every column can see it, because what one
  // column offers follows from what the others have been narrowed down to.
  const [picked, setPicked] = React.useState<string[][]>([]);
  // Stable, so that a column rebuilt below keeps reaching this one, and quiet
  // when nothing moved: the dropdowns say what they hold on every render of
  // the filter row, and each of those must not be another render.
  const pick = React.useCallback((index: number, values: string[]) => {
    setPicked(current => {
      const ticked = current[index] ?? [];
      const same =
        ticked.length === values.length &&
        ticked.every((value, at) => value === values[at]);
      if (same) {
        return current;
      }
      const next = current.slice();
      next[index] = values;
      return next;
    });
  }, []);

  // What each value of each column is worth in rows under the other columns'
  // filters, which is both what the dropdowns put at the end of their lines
  // and which values they have left to offer. Counted on every render and
  // handed on as the ref itself, which the columns below read when they
  // draw - see filterCountOf and filterOptionsOf there.
  const counts = React.useRef<Map<string, number>[]>([]);
  counts.current = filterCountsOf(columns, rows, picked);

  // Hold on to the same columns for as long as they say the same thing, so
  // that a render of whatever stands around the table does not empty its
  // filter row - see signatureOf above.
  const signature = signatureOf(columns, rows, labels);
  const tableColumns = React.useMemo(
    () => withFilterRow(columns, rows, labels, counts, pick),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signature],
  );

  // Held on to for as long as the title is the same one: a component type the
  // table has not seen before is a new toolbar to it, which throws away what
  // the filter row holds the way a new set of columns does.
  const components = React.useMemo(
    () => (countInTitle ? { Toolbar: countingToolbar(title) } : undefined),
    [countInTitle, title],
  );

  return (
    <>
      {held.length > 0 && (
        <Box display="flex" justifyContent="flex-end">
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={showArchived}
                onChange={event => setShowArchived(event.target.checked)}
              />
            }
            label="Show archived"
          />
        </Box>
      )}
      <Table<T>
        title={title}
        columns={tableColumns}
        data={rows}
        components={components}
        style={{
          minWidth: '0',
          width: '100%',
          ...(variant === 'gridItem' ? { height: 'calc(100% - 10px)' } : {}),
        }}
        options={{
          search: false,
          paging: false,
          actionsColumnIndex: -1,
          padding: 'dense',
          draggable: false,
          filtering: columns.some(column => column.filterValueOf),
          ...tableOptions,
        }}
      />
    </>
  );
}

/** A product that has been retired, a team or a department wound up. */
function isArchivedByTag(entity: Entity): boolean {
  return entity.metadata.tags?.includes(ARCHIVED_TAG) ?? false;
}

/** @public */
export type RelatedEntitiesCardProps<T extends Entity> = {
  variant?: InfoCardVariants;
  title: string;
  columns: FilterableColumn<T>[];
  entityKind?: string;
  relationType?: string;
  asRenderableEntities: (entities: Entity[]) => T[];
  tableOptions?: TableOptions;
  /**
   * Which rows have been wound up, where the tag on the entity is not the
   * answer - see `EntityFilterTable`. A resource is the case: it carries no
   * such tag, the product behind it does.
   */
  isArchived?: (entity: T) => boolean;
  /** Whether the title says how many rows there are - see `EntityFilterTable`. */
  countInTitle?: boolean;
};

export function RelatedEntitiesCard<T extends Entity>(
  props: Readonly<RelatedEntitiesCardProps<T>>,
) {
  const {
    variant = 'gridItem',
    title,
    columns,
    entityKind,
    relationType,
    asRenderableEntities,
    tableOptions = {},
    isArchived = isArchivedByTag,
    countInTitle = false,
  } = props;

  const { entity } = useEntity();
  const { entities, loading, error } = useRelatedEntities(entity, {
    type: relationType,
    kind: entityKind,
  });

  // Without a default sort the rows keep the arbitrary order the catalog
  // returns them in. The first column is the entity name everywhere, so sort
  // by it unless the caller already picked a default.
  const sortedColumns = columns.map((column, index) =>
    index === 0 && !column.defaultSort
      ? { ...column, defaultSort: 'asc' as const }
      : column,
  );

  if (loading) {
    return (
      <InfoCard variant={variant} title={title}>
        <Progress />
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard variant={variant} title={title}>
        <ResponseErrorPanel error={error} />
      </InfoCard>
    );
  }

  const related = asRenderableEntities(entities || []);

  // A card with nothing in it leaves itself out, the way a team that owns no
  // product has no Products card. What has been wound up still counts as
  // something: the table holds those rows back but keeps the switch that
  // brings them out, and a card that left itself out would take it with it.
  if (related.length === 0) {
    return <></>;
  }

  return (
    <EntityFilterTable
      title={title}
      variant={variant}
      columns={sortedColumns}
      entities={related}
      isArchived={isArchived}
      countInTitle={countInTitle}
      // A third click on a header would otherwise clear the sorting and drop
      // the rows back into catalog order while the arrow still points up,
      // which looks broken. Toggle between asc and desc instead.
      tableOptions={{ thirdSortClick: false, ...tableOptions }}
    />
  );
}
